import { WebhookEvents } from 'vopay';
import { useCallback, useRef, useState } from 'react';
import { api } from '@/services/api.config';

/**
 * `GET /partner/webhook-url/info` for the partner account whose bearer is given: the events it is
 * subscribed to, each with its URL. The route is self-scoped, so a parent reads one of its partner
 * accounts by signing the request with that account's own token. The list arrives as an object
 * keyed by index (`JSON_FORCE_OBJECT`) and is stored as an array.
 */
export default function useFetchPartnerWebhookInfo() {
  const [fetchingPartnerWebhooks, setFetchingPartnerWebhooks] = useState(false);
  const [partnerWebhooks, setPartnerWebhooks] = useState<WebhookEvents[]>();

  /**
   * Only the newest request may write to state. The partner page refetches as the user moves from
   * one partner account to another, and nothing cancels the request already in flight — so without
   * this an earlier response landing last would put one partner's subscriptions under another's name.
   */
  const latestRequestId = useRef(0);

  const fetchPartnerWebhookInfo = useCallback(async (bearer: string) => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetchingPartnerWebhooks(true);
      const res = await api.fetchPartnerWebhook({}, undefined, bearer);
      if (!res.Success) throw new Error(res.ErrorMessage);
      if (isLatest()) setPartnerWebhooks(Object.values(res.Webhooks));

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetchingPartnerWebhooks(false);
    }
  }, []);

  const reset = useCallback(() => {
    // A reset supersedes whatever is in flight, so that response can no longer land on the cleared state.
    latestRequestId.current += 1;
    setPartnerWebhooks(undefined);
    setFetchingPartnerWebhooks(false);
  }, []);

  return { fetchingPartnerWebhooks, partnerWebhooks, fetchPartnerWebhookInfo, reset };
}
